/**
 * Maximum allowed length for CSS custom property values transported to iframe.
 *
 * NOTE: This value and the blocked-token policy intentionally mirror
 * `packages/shared-types/src/schemas/embed-bridge.ts` so validation happens
 * both before transport (host) and at contract parse-time (iframe).
 *
 * CONTROL_CHAR_PATTERN here is intentionally more permissive than
 * embed-bridge.ts: it allows tabs/newlines/CR/FF because
 * normalizeCssFormattingWhitespace converts them to spaces before the
 * control-char check. The iframe-side schema (embed-bridge.ts) uses
 * the stricter pattern as defense-in-depth for values that bypass
 * host-side sanitization.
 */
export const MAX_CSS_VARIABLE_VALUE_LENGTH = 1024
export const MAX_HOST_FONT_CSS_LENGTH = 16_384

const CONTROL_CHAR_PATTERN = /[\u0000-\u0008\u000b\u000e-\u001f\u007f]/u
const FONT_RULES_PATTERN = /^(?:\s*@font-face\s*\{[^{}]*\}\s*)+$/iu
const CSS_COMMENT_PATTERN = /\/\*[\s\S]*?\*\//g
const CSS_FORMATTING_WHITESPACE_PATTERN = /[\t\n\r\f]+/gu

/**
 * Normalizes CSS values for dangerous-token scanning.
 * Lowercases and removes CSS comments/whitespace/control chars to catch obfuscation.
 */
function normalizeForSecurityScan(value: string): string {
	const withoutComments = value.replace(CSS_COMMENT_PATTERN, '')
	return withoutComments.toLowerCase().replace(/[\s\u0000-\u001f\u007f]+/gu, '')
}

/**
 * Collapses CSS formatting whitespace (tabs, newlines, carriage returns, form feeds)
 * into single spaces. Applied to the output value before trimming, so multiline
 * host-provided values (e.g., font stacks) become single-line without losing
 * meaningful token boundaries. This also ensures split-token obfuscation
 * (e.g., "java\nscript:") is collapsed before the security scan.
 */
function normalizeCssFormattingWhitespace(value: string): string {
	return value.replace(CSS_FORMATTING_WHITESPACE_PATTERN, ' ')
}

/**
 * Sanitizes host-provided CSS variable values before iframe transport.
 * Returns `undefined` for unsafe or invalid values.
 *
 * @param value - The raw CSS property value.
 * @param variableName - Optional variable name for diagnostic logging.
 */
export function sanitizeCssVariableValue(value: string, variableName?: string): string | undefined {
	const trimmed = normalizeCssFormattingWhitespace(value).trim()
	if (!trimmed) return undefined
	if (trimmed.length > MAX_CSS_VARIABLE_VALUE_LENGTH) {
		console.warn(`[Char] CSS variable ${variableName ?? '(unknown)'} rejected: value exceeds max length (${trimmed.length} > ${MAX_CSS_VARIABLE_VALUE_LENGTH})`)
		return undefined
	}
	if (CONTROL_CHAR_PATTERN.test(trimmed)) {
		console.warn(`[Char] CSS variable ${variableName ?? '(unknown)'} rejected: contains control characters`)
		return undefined
	}

	const normalized = normalizeForSecurityScan(trimmed)
	const blockedToken = getBlockedCssVariableToken(normalized)
	if (blockedToken) return warnAndReject(variableName, blockedToken)

	return trimmed
}

/**
 * Sanitizes host-provided font CSS before iframe injection.
 * Only `@font-face` rules are allowed.
 */
export function sanitizeHostFontCss(fontCss: string): string | undefined {
	const trimmed = fontCss.trim()
	if (!trimmed) return undefined
	if (trimmed.length > MAX_HOST_FONT_CSS_LENGTH) {
		console.warn(`[Char] Host font CSS rejected: value exceeds max length (${trimmed.length} > ${MAX_HOST_FONT_CSS_LENGTH})`)
		return undefined
	}
	if (CONTROL_CHAR_PATTERN.test(trimmed)) {
		console.warn('[Char] Host font CSS rejected: contains control characters')
		return undefined
	}

	const normalized = normalizeForSecurityScan(trimmed)
	const blockedToken = getBlockedFontToken(normalized)
	if (blockedToken) {
		console.warn(`[Char] Host font CSS rejected: contains blocked token "${blockedToken}"`)
		return undefined
	}

	const withoutComments = trimmed.replace(/\/\*[\s\S]*?\*\//g, '').trim()
	if (!FONT_RULES_PATTERN.test(withoutComments)) {
		console.warn('[Char] Host font CSS rejected: only @font-face rules are allowed')
		return undefined
	}

	return trimmed
}

function getBlockedCssVariableToken(normalizedValue: string): string | undefined {
	if (normalizedValue.includes('javascript:')) return 'javascript:'
	if (normalizedValue.includes('vbscript:')) return 'vbscript:'
	if (normalizedValue.includes('expression(')) return 'expression('
	if (normalizedValue.includes('@import')) return '@import'
	if (normalizedValue.includes('-moz-binding')) return '-moz-binding'
	if (normalizedValue.includes('url(')) return 'url('
	return undefined
}

function getBlockedFontToken(normalizedValue: string): string | undefined {
	if (normalizedValue.includes('javascript:')) return 'javascript:'
	if (normalizedValue.includes('vbscript:')) return 'vbscript:'
	if (normalizedValue.includes('expression(')) return 'expression('
	if (normalizedValue.includes('-moz-binding')) return '-moz-binding'
	return undefined
}

function warnAndReject(variableName: string | undefined, blockedToken: string): undefined {
	console.warn(`[Char] CSS variable ${variableName ?? '(unknown)'} rejected: contains blocked token "${blockedToken}"`)
	return undefined
}
